// F1 Data Enhancement Module
// Handles data enhancement logic like adding circuit names, winners, and recent winners

/**
 * Add circuit names to races
 * @param {Object} data - F1 data object
 * @returns {Object} - Enhanced data with circuit names in races
 */
export const enhanceRacesWithCircuits = (data) => {
  if (!data.races || !data.circuits) return data;

  const circuitNameMap = {};
  data.circuits.forEach(circuit => {
    circuitNameMap[circuit.circuitId] = circuit.name;
  });

  data.races = data.races.map(race => ({
    ...race,
    circuit: circuitNameMap[race.circuitId] || `Circuit ${race.circuitId}`
  }));

  return data;
};

/**
 * Add winner and top drivers information to races
 * @param {Object} data - F1 data object
 * @returns {Object} - Enhanced data with winner and podium info
 */
export const enhanceRacesWithWinners = (data) => {
  if (!data.races || !data.results || !data.drivers) return data;

  // Create driver name map
  const driverNameMap = {};
  data.drivers.forEach(driver => {
    driverNameMap[driver.driverId] = `${driver.forename} ${driver.surname}`;
  });

  // Group results by race to get podium finishers
  const raceResults = {};
  data.results.forEach(result => {
    const raceId = result.raceId;
    if (!raceResults[raceId]) {
      raceResults[raceId] = [];
    }
    raceResults[raceId].push(result);
  });

  // Process each race to get winner and podium
  data.races = data.races.map(race => {
    const results = raceResults[race.raceId] || [];
    
    // Sort by position (1=first place)
    const sortedResults = results.sort((a, b) => a.position - b.position);
    
    // Get winner (position = 1)
    const winner = sortedResults.find(r => r.position === 1);
    const winnerInfo = winner ? {
      driverId: winner.driverId,
      driverName: driverNameMap[winner.driverId] || `Driver ${winner.driverId}`,
      constructorId: winner.constructorId,
      year: race.year
    } : null;

    // Get top 3 drivers (podium) with race times
    const topDrivers = sortedResults
      .filter(r => r.position >= 1 && r.position <= 3)
      .map(result => ({
        driverId: result.driverId,
        driverName: driverNameMap[result.driverId] || `Driver ${result.driverId}`,
        position: result.position,
        time: result.time || 'Unknown'
      }));

    return {
      ...race,
      winner: winnerInfo?.driverName || 'Unknown',
      winnerDriverId: winnerInfo?.driverId || null,
      topDrivers: topDrivers
    };
  });

  return data;
};

/**
 * Add recent winners to circuits
 * @param {Object} data - F1 data object
 * @returns {Object} - Enhanced data with recent winner info
 */
export const enhanceCircuitsWithRecentWinners = (data) => {
  if (!data.circuits || !data.races || !data.results || !data.drivers) return data;

  // Create driver name map
  const driverNameMap = {};
  data.drivers.forEach(driver => {
    driverNameMap[driver.driverId] = `${driver.forename} ${driver.surname}`;
  });

  // Find winner for each race (position = 1)
  const raceWinners = {};
  data.results.forEach(result => {
    if (result.position === 1) {
      const race = data.races?.find(r => r.raceId === result.raceId);
      if (race) {
        raceWinners[result.raceId] = {
          driverId: result.driverId,
          driverName: driverNameMap[result.driverId] || `Driver ${result.driverId}`,
          year: race.year,
          circuitId: race.circuitId
        };
      }
    }
  });

  // Group winners by circuit and get the most recent winner
  const circuitWinners = {};
  Object.values(raceWinners).forEach(winner => {
    const circuitId = winner.circuitId;
    if (!circuitWinners[circuitId] || winner.year > circuitWinners[circuitId].year) {
      circuitWinners[circuitId] = winner;
    }
  });

  // Add recent winner info to circuits
  data.circuits = data.circuits.map(circuit => ({
    ...circuit,
    recentWinner: circuitWinners[circuit.circuitId]?.driverName || 'No recent winner',
    recentWinnerDriverId: circuitWinners[circuit.circuitId]?.driverId || null,
    recentWinnerYear: circuitWinners[circuit.circuitId]?.year || null
  }));

  return data;
};

/**
 * Add race count to circuits
 * @param {Object} data - F1 data object
 * @returns {Object} - Enhanced data with race count
 */
export const enhanceCircuitsWithRaceCount = (data) => {
  if (!data.circuits || !data.races) return data;

  const circuitRaceCount = {};
  data.races.forEach(race => {
    const circuitId = race.circuitId;
    if (!circuitRaceCount[circuitId]) circuitRaceCount[circuitId] = 0;
    circuitRaceCount[circuitId]++;
  });

  data.circuits = data.circuits.map(circuit => ({
    ...circuit,
    raceCount: circuitRaceCount[circuit.circuitId] || 0
  }));

  return data;
};

/**
 * Apply all data enhancements
 * @param {Object} data - F1 data object
 * @returns {Object} - Fully enhanced data
 */
export const applyDataEnhancements = (data) => {
  let enhancedData = { ...data };
  
  enhancedData = enhanceRacesWithCircuits(enhancedData);
  enhancedData = enhanceCircuitsWithRaceCount(enhancedData);
  enhancedData = enhanceRacesWithWinners(enhancedData);
  enhancedData = enhanceCircuitsWithRecentWinners(enhancedData);
  
  return enhancedData;
};
